import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Inbox, Send, BadgeCheck, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { T } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InboxPage() {
  const { user, login, loading } = useAuth();
  const [data, setData] = useState(null);
  const [replyFor, setReplyFor] = useState(null);
  const [reply, setReply] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/inquiries`, { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const respond = async (id) => {
    if (!reply.trim()) return;
    try {
      await axios.post(`${API}/inquiries/${id}/respond`, { response: reply }, { withCredentials: true });
      toast.success("Response saved");
      setReplyFor(null);
      setReply("");
      load();
    } catch {
      toast.error("Could not save response");
    }
  };

  if (!loading && !user) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <Inbox className="mx-auto h-10 w-10 text-[#0055FF]" />
        <h1 className="mt-4 text-2xl font-bold text-slate-900"><T>Inquiry Inbox</T></h1>
        <p className="mt-2 text-sm text-slate-600"><T>Sign in to view your marketplace inquiries and responses.</T></p>
        <button data-testid="inbox-login-button" onClick={login} className="mt-5 rounded-full bg-[#0055FF] px-6 py-2.5 text-sm font-bold text-white hover:bg-[#0040CC]"><T>Join SportsPulse</T></button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6" id="page-content">
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#0055FF] text-white"><Inbox className="h-5 w-5" /></span>
        <div>
          <h1 className="text-2xl font-bold text-slate-900"><T>Inquiry Inbox</T></h1>
          <p className="text-sm text-slate-500">
            {data?.role === "owner" ? <T>Site owner view — all marketplace inquiries</T> : <T>Your marketplace inquiries and provider responses</T>}
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-4" data-testid="inquiries-list">
        {(data?.inquiries || []).map((q) => (
          <div key={q.inquiry_id} data-testid={`inquiry-${q.inquiry_id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-blue-50 px-2.5 py-1 text-xs font-bold text-[#0055FF]">{q.listing_title}</span>
              {q.status === "answered" ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700"><BadgeCheck className="h-3 w-3" /><T>Answered</T></span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-bold text-amber-700"><Clock className="h-3 w-3" /><T>Awaiting response</T></span>
              )}
              <span className="text-xs text-slate-400">{new Date(q.created_at).toLocaleString()}</span>
            </div>
            <p className="mt-3 text-sm text-slate-700">{q.message}</p>
            {data?.role === "owner" && (
              <p className="mt-2 text-xs text-slate-500"><T>From</T>: {q.user_name} {q.user_email && `· ${q.user_email}`} {q.contact && `· ${q.contact}`}</p>
            )}
            {q.response && (
              <div className="mt-3 rounded-xl bg-emerald-50 px-4 py-3">
                <p className="text-xs font-bold uppercase text-emerald-700"><T>Response</T></p>
                <p className="mt-1 text-sm text-slate-700">{q.response}</p>
              </div>
            )}
            {data?.role === "owner" && q.status !== "answered" && (
              replyFor === q.inquiry_id ? (
                <div className="mt-3 flex gap-2">
                  <input data-testid={`respond-input-${q.inquiry_id}`} value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Write a response…" className="flex-1 rounded-xl border border-slate-200 px-4 py-2 text-sm outline-none focus:border-[#0055FF]" />
                  <button data-testid={`respond-send-${q.inquiry_id}`} onClick={() => respond(q.inquiry_id)} className="rounded-xl bg-[#0055FF] px-4 py-2 text-white hover:bg-[#0040CC]"><Send className="h-4 w-4" /></button>
                </div>
              ) : (
                <button data-testid={`respond-open-${q.inquiry_id}`} onClick={() => { setReplyFor(q.inquiry_id); setReply(""); }} className="mt-3 rounded-full border border-[#0055FF] px-4 py-2 text-sm font-bold text-[#0055FF] hover:bg-blue-50"><T>Respond</T></button>
              )
            )}
          </div>
        ))}
        {data && data.inquiries.length === 0 && (
          <p className="py-12 text-center text-sm text-slate-500" data-testid="inbox-empty"><T>No inquiries yet. Browse the marketplace on the Business page to contact a provider.</T></p>
        )}
      </div>
    </div>
  );
}
